import { Component, EventEmitter, Input, OnChanges, Output, ViewEncapsulation, forwardRef } from '@angular/core';
import { FormsModule, NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { MaterialModule } from '@app/material.module';

/**
 * Select con autocompletado: se escribe para filtrar (sin acentos ni
 * mayúsculas) y SIEMPRE se selecciona una opción de la lista — al salir del
 * campo, si el texto no corresponde a una opción elegida, se restaura la
 * selección anterior, así el valor nunca queda "a medias".
 *
 * El valor del control es el `id` de la opción (o null). Funciona con
 * ngModel y con formControlName. `options` son objetos con `id` y `name`
 * (o se pasa `labelFn` para armar la etiqueta).
 */
@Component({
  standalone: true,
  selector: 'app-auto-select',
  imports: [FormsModule, MaterialModule],
  encapsulation: ViewEncapsulation.None,
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => AutoSelectComponent), multi: true },
  ],
  template: `
    <mat-form-field class="auto-select-field">
      <mat-label>{{ label }}</mat-label>
      <!-- Sin ngModel: el trigger del autocomplete escribe la etiqueta al
           elegir (displayWith) y aquí solo se escucha lo que se TECLEA. -->
      <input matInput #inp [value]="searchText" [disabled]="disabled"
             [matAutocomplete]="panel"
             (input)="onInput(inp.value)"
             (focus)="onFocus()" (blur)="onTouched()">
      <mat-icon matSuffix class="auto-select-arrow">arrow_drop_down</mat-icon>
      <mat-autocomplete #panel="matAutocomplete" [displayWith]="displayWith"
                        (optionSelected)="onSelected($event.option.value)"
                        (closed)="onClosed()">
        @if (allowEmpty) {
          <mat-option [value]="null">{{ emptyLabel }}</mat-option>
        }
        @for (option of view; track option.id) {
          <mat-option [value]="option.id">{{ optionLabel(option) }}</mat-option>
        }
        @if (view.length === 0) {
          <mat-option disabled [value]="undefined">Sin coincidencias</mat-option>
        }
      </mat-autocomplete>
    </mat-form-field>
  `,
  styles: [`
    .auto-select-field { width: 100%; }
    .auto-select-arrow { color: #607d8b; }
  `],
})
export class AutoSelectComponent implements ControlValueAccessor, OnChanges {
  @Input() label = '';
  @Input() options: any[] = [];
  /** Etiqueta de cada opción; por omisión su name. */
  @Input() labelFn: (option: any) => string = (option: any) => String(option?.name ?? '');
  /** Muestra una primera opción que deja el valor en null. */
  @Input() allowEmpty = false;
  @Input() emptyLabel = '—';

  /** Se emite después de que cambia el valor (para reaccionar como con selectionChange). */
  @Output() changed = new EventEmitter<any>();

  public value: any = null;
  public disabled = false;
  public searchText = '';
  /** Opciones visibles. Es un arreglo ESTABLE (no un getter): si la lista
   *  se rehace en cada detección de cambios, el primer click sobre una
   *  opción se pierde y había que picarle varias veces. */
  public view: any[] = [];

  private onChange: (value: any) => void = () => {};
  public onTouched: () => void = () => {};
  private optionsSignature = '';

  private static normalize(text: any): string {
    return String(text ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toLowerCase();
  }

  /** El panel escribe el valor (id) en el input: esto lo traduce a etiqueta. */
  public displayWith = (id: any): string => this.labelOf(id);

  public optionLabel(option: any): string {
    return this.labelFn(option);
  }

  private labelOf(id: any): string {
    if (id == null) return '';
    const option = (this.options ?? []).find(o => o?.id === id);
    return option ? this.optionLabel(option) : '';
  }

  private all(): any[] {
    return (this.options ?? []).filter(o => o != null);
  }

  /** Recalcula la lista visible; `needle` vacío = lista completa. */
  private refresh(needle = ''): void {
    const n = AutoSelectComponent.normalize(needle);
    this.view = n ? this.all().filter(o => AutoSelectComponent.normalize(this.optionLabel(o)).includes(n)) : this.all();
  }

  onInput(text: string): void {
    this.searchText = text;
    this.refresh(text);
  }

  onFocus(): void {
    // Al entrar se muestra la lista completa aunque haya texto seleccionado.
    this.refresh();
  }

  onSelected(id: any): void {
    this.value = id ?? null;
    this.searchText = this.labelOf(this.value);
    this.onChange(this.value);
    this.onTouched();
    this.changed.emit(this.value);
  }

  /** Al cerrar el panel sin elegir, el texto regresa a la opción elegida. */
  onClosed(): void {
    this.searchText = this.labelOf(this.value);
    this.refresh();
  }

  ngOnChanges(): void {
    // Muchos padres pasan getters que devuelven un arreglo nuevo en cada
    // ciclo: solo se recalcula si de verdad cambiaron las opciones.
    const sig = this.all().map(o => `${o.id}|${this.optionLabel(o)}`).join('\u0001');
    if (sig === this.optionsSignature) return;
    this.optionsSignature = sig;
    this.refresh();
    this.searchText = this.labelOf(this.value);
  }

  // ── ControlValueAccessor ──
  writeValue(value: any): void {
    this.value = value ?? null;
    this.searchText = this.labelOf(this.value);
  }
  registerOnChange(fn: any): void { this.onChange = fn; }
  registerOnTouched(fn: any): void { this.onTouched = fn; }
  setDisabledState(isDisabled: boolean): void { this.disabled = isDisabled; }
}
